/**
 * FleetHub B2B access gating (client-side, advisory).
 *
 * The platform runs a **single** Firebase Auth project (`liria-ai-inspect-backend`)
 * shared by internal/ops accounts, B2B FleetHub customers and admins. That means
 * `signInWithEmailAndPassword` on the public FleetHub form succeeds for *any* of
 * them. Before PLA-534 nothing on the client checked *which kind* of user had just
 * signed in, so an internal ops account could land on the FleetHub dashboard where
 * every callable answered `permission-denied` / "Kein gültiges B2B-Kundenkonto"
 * (CARASSIST-WEB-2T, and CARASSIST-WEB-V three months earlier).
 *
 * This module is that missing gate. It is deliberately **advisory** — the server
 * (`requireB2bCustomer` in `functions/src/helpers/auth-helpers.ts`) remains the
 * sole authority on B2B access. The client gate exists to keep the wrong kind of
 * user off a dashboard that can only 403, not to enforce security.
 *
 * @module services/firebase/b2bAccess
 */

import type { User } from "firebase/auth"

/** Shown on the login screen when a non-B2B account authenticates successfully. */
export const FLEETHUB_WRONG_ACCOUNT_MESSAGE =
  "Dieses Konto ist kein FleetHub-Kundenkonto. Bitte melde dich mit deinen FleetHub-Zugangsdaten an."

/** Shown after an active session is rejected by the backend's B2B guard. */
export const FLEETHUB_ACCESS_REVOKED_MESSAGE =
  "Dein Zugang zu FleetHub ist nicht mehr gültig. Bitte melde dich erneut an."

/**
 * Backend messages thrown by `requireB2bCustomer`, all as `permission-denied`.
 * Kept as substrings because the callable rejection is rewrapped for display and
 * only the message survives verbatim.
 *
 * Source: `Liria-Inspect-Application/functions/src/helpers/auth-helpers.ts`
 *   :297 no `b2bCustomerUsers/{uid}` doc  → "Kein gültiges B2B-Kundenkonto"
 *   :308 / :235 account deactivated       → "Konto deaktiviert"
 *   :314 / :240 no customerNumber          → "Kein Kundenkonto verknüpft"
 */
const B2B_GUARD_MESSAGES: readonly string[] = [
  "Kein gültiges B2B-Kundenkonto",
  "Konto deaktiviert",
  "Kein Kundenkonto verknüpft",
]

/**
 * Decides whether a freshly-authenticated user must be kept out of FleetHub.
 *
 * **Fails open by design.** `requireB2bCustomer` carries a Firestore fallback
 * precisely because the `userType` claim can legitimately be absent — legacy
 * accounts, accounts provisioned before claims existed, or a claim wiped by a
 * wholesale `setCustomUserClaims` overwrite (the hazard documented at
 * `auth-helpers.ts:74-75`, where the whole claim object is replaced rather than
 * merged). Gating on `userType === "b2b"` would therefore lock out real customers
 * whose claim was never backfilled.
 *
 * So we block only on a **positively wrong** claim, and let an absent claim
 * through to the server, which is the authority. Anything that slips past here is
 * caught by the `permission-denied` recovery path (see `isB2bAccessDenied`).
 *
 * @param user - The signed-in Firebase user.
 * @returns `true` only when the token positively declares a non-B2B user type.
 */
export async function isBlockedFromFleetHub(user: User): Promise<boolean> {
  try {
    const { claims } = await user.getIdTokenResult()
    const userType = claims.userType

    // Absent / non-string claim → fail open, let the server decide.
    if (typeof userType !== "string" || userType.length === 0) return false

    return userType !== "b2b"
  } catch {
    // Token fetch failed (offline, clock skew, revoked refresh token).
    // Fail open — the callable will reject anyway if access is genuinely absent.
    return false
  }
}

/**
 * True when a callable rejection is the backend's B2B guard refusing this user.
 *
 * Distinguishes "you are not a valid B2B customer" — which must sign the session
 * out — from every other `permission-denied` (e.g. an order that belongs to a
 * different customer), which must not.
 *
 * Accepts both the namespaced `functions/permission-denied` that the Firebase JS
 * SDK actually produces and the bare `permission-denied`, so it keeps working
 * regardless of the switch-statement cleanup tracked separately on PLA-534.
 * Reads the message from the error and from its `cause`, because display rewraps
 * replace the message while `rewrapCallableError` preserves the original as cause.
 */
export function isB2bAccessDenied(error: unknown): boolean {
  if (!error || typeof error !== "object") return false

  const code = (error as { code?: unknown }).code
  const isPermissionDenied = code === "functions/permission-denied" || code === "permission-denied"
  if (!isPermissionDenied) return false

  const messages: string[] = []
  const own = (error as { message?: unknown }).message
  if (typeof own === "string") messages.push(own)
  const cause = (error as { cause?: unknown }).cause
  if (cause && typeof cause === "object") {
    const causeMessage = (cause as { message?: unknown }).message
    if (typeof causeMessage === "string") messages.push(causeMessage)
  }

  return messages.some((message) => B2B_GUARD_MESSAGES.some((guard) => message.includes(guard)))
}
